import React, { useState, useEffect, useContext } from 'react';
import { Layout } from 'antd';
import { useSelector } from 'react-redux';
import type { RootState } from '../redux/store';
import SidebarMenu from './SidebarMenu';
import HeaderBar from './HeaderBar';
import SetupBanner from './SetupBanner';
import TrialBanner from './TrialBanner';

const { Content } = Layout;

/**
 * True when a DashboardLayout is already mounted above in the tree. Lets pages
 * that wrap themselves in <DashboardLayout> be embedded inside another shell
 * (e.g. the Attendance workspace tabs) without rendering a second sidebar/header
 * — the nested layout becomes a passthrough. Keeps existing pages working as-is.
 */
export const DashboardLayoutContext = React.createContext(false);

interface DashboardLayoutProps {
    children: React.ReactNode;
}

/** Returns pixel padding for the content area based on viewport width */
function getContentPadding(width: number): string {
    if (width <= 375) return '4px';
    if (width <= 480) return '6px';
    if (width <= 640) return '8px';
    if (width <= 768) return '10px';
    if (width <= 900) return '12px';
    if (width <= 1100) return '14px';
    if (width <= 1280) return '16px';
    if (width <= 1440) return '20px';
    if (width <= 1600) return '24px';
    return '28px'; // 1600px+ large screens
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
    const alreadyInsideLayout = useContext(DashboardLayoutContext);
    // Embedded (e.g. inside the Attendance workspace tabs): render just the page
    // body, no second shell. Hooks live in DashboardLayoutShell so they are never
    // called conditionally here.
    if (alreadyInsideLayout) {
        return <>{children}</>;
    }
    return <DashboardLayoutShell>{children}</DashboardLayoutShell>;
};

const DashboardLayoutShell: React.FC<DashboardLayoutProps> = ({ children }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
    const role = (useSelector((state: RootState) => (state.auth as any).activeRole) || localStorage.getItem('activeRole') || '');

    useEffect(() => {
        const checkViewport = () => {
            const w = window.innerWidth;
            setViewportWidth(w);

            // isMobile=true means: use position:fixed overlay drawer sidebar (0-width when collapsed)
            // Covers: real phones/tablets (≤768px) AND high-zoom laptops (150%+ on 1280px = ≤900px)
            const mobile = w <= 900;
            setIsMobile(mobile);

            // Auto-collapse at ≤ 900px
            if (w <= 900) {
                setCollapsed(true);
            } else {
                setCollapsed(prev => prev && w > 900 ? false : prev);
            }
        };

        checkViewport();
        window.addEventListener('resize', checkViewport);
        return () => window.removeEventListener('resize', checkViewport);
    }, []);

    const contentPadding = getContentPadding(viewportWidth);

    return (
        <DashboardLayoutContext.Provider value={true}>
        <Layout
            className="dashboard-shell"
            style={{
                height: '100vh',
                overflow: 'hidden',
                flexDirection: 'row',
            }}
        >
            <SidebarMenu
                darkMode={false}
                collapsed={collapsed}
                onCollapse={setCollapsed}
                userRole={role}
                isMobile={isMobile}
            />

            <Layout style={{
                background: '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                minWidth: 0,
                overflow: 'hidden',
                flex: 1,
            }}>
                <HeaderBar collapsed={collapsed} onToggle={() => setCollapsed(c => !c)} />
                <TrialBanner />
                <SetupBanner />

                <Content
                    style={{
                        margin: 0,
                        padding: contentPadding,
                        background: '#FFFFFF',
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        minWidth: 0,
                    }}
                >
                    {children}
                </Content>
            </Layout>
        </Layout>
        </DashboardLayoutContext.Provider>
    );
};

export default DashboardLayout;

